import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller, FieldValues } from "react-hook-form";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Grid, Typography } from "@mui/material";
import dayjs from "dayjs";

import { FormInputProps } from "./FormInputProps";
import colors from "../../styles/colors/colors";
import fontWeight from "../../styles/mui/fontWeight";
import { DateFormats, ValidYear } from "../../constants/Date";
import useMenuItem from "../../hooks/useMenuItem";

const DatePicker =<TFieldValues extends FieldValues> ({
  allowFutureDate,
  control,
  disabled,
  helper,
  label,
  maximumdate,
  minimumDate,
  name,
  onChangeHandler,
  InputStyle,
  readOnly,
}: FormInputProps<TFieldValues>) => {

  const { open, onOpen, onClose } = useMenuItem();

  const toDayjs = (raw: unknown) => {
    if (typeof raw === "string") {
      return dayjs(raw);
    }
    if (dayjs.isDayjs(raw)) {
      return raw;
    }
    return dayjs(raw as dayjs.ConfigType);
  };

  const isValidHistoricDate = (d: dayjs.Dayjs) =>
    d.isValid() && d.year() >= ValidYear;

  const validateRange = (
    d: dayjs.Dayjs,
    min?: dayjs.Dayjs | null,
    max?: dayjs.Dayjs | null
  ): string | undefined => {
    if (max?.isValid() && d.isAfter(max, "day")) {
      return `Allowed till ${max.format(DateFormats.MM_DD_YYYY)}`;
    }
    if (min?.isValid() && d.isBefore(min, "day")) {
      return `Allowed from ${min.format(DateFormats.MM_DD_YYYY)}`;
    }
    return undefined;
  };

  const isFutureDay = (d: dayjs.Dayjs) => d.isAfter(dayjs(), "day");

  // Refactored: reduced branching / nesting
  const customDateValidator = (raw: unknown) => {
    if (raw == null || raw === "") return true;

    const dateObj = toDayjs(raw);
    if (!isValidHistoricDate(dateObj)) return "Invalid date";

    const rangeMessage = validateRange(dateObj, minimumDate, maximumdate);
    if (rangeMessage) return rangeMessage;

    if (!allowFutureDate && isFutureDay(dateObj)) {
      return "Future date not allowed";
    }

    return true;
  };

  return (
    <>
      <Grid>
        <Controller
          name={name}
          control={control}
          rules={{ ...helper, ...{ validate: customDateValidator } }}
          render={({
            field: { onChange, value, ref, onBlur, ...fieldProps },
            fieldState: { error },
          }) => {
            return (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  {...fieldProps}
                  onClose={onClose}
                  open={open}
                  onOpen={onOpen}
                  disabled={disabled ? disabled : false}
                  readOnly={readOnly}
                  inputRef={ref}
                  data-testid={name}
                  label={
                    label ? (
                      <Typography
                        variant="h5"
                        fontWeight={fontWeight.Weight[3]}
                        color={colors.fonts[2]}
                      >
                        {label}
                      </Typography>
                    ) : (
                      label
                    )
                  }
                  value={value ? dayjs(value) : null}
                  onChange={(e) => {
                    onChangeHandler && onChangeHandler(e);
                    onChange(e);
                  }}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      required:
                        !!helper?.required,
                      error: error !== undefined,
                      helperText: error ? error.message : null,
                      onBlur: onBlur,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  format="MM/DD/YYYY"
                  maxDate={!allowFutureDate ? dayjs(new Date()) : maximumdate}
                  minDate={minimumDate}
                  disableFuture={!allowFutureDate}
                  sx={InputStyle}
                />
              </LocalizationProvider>
            );
          }}
        />
      </Grid>
    </>
  );
};

export default DatePicker;
